import type { DocNode } from '@atlaskit/adf-schema';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { getFlagProvider } from '../utils/flagProvider.tsx';

import type { PostRobotEvent, PostRobotOnFn } from './__types__/postRobot.ts';

const CONNECT_MACRO_EXTENSION_TYPE = 'com.atlassian.confluence.macro.core';
const FORGE_EXTENSION_TYPE = 'com.atlassian.ecosystem';
const EMBEDDED_FAUS_MESSAGE_PREFIX = 'messageFromEmbeddedFAUS_';
const EMBEDDED_FAUS_ANALYTICS_PREFIX = 'analyticsFromEmbeddedFAUS_';
const EMBEDDED_FAUS_FETCH_APP_KEY_PREFIX = 'fetchAppKeyFromEmbeddedFAUS_';

let postRobotOn: PostRobotOnFn | null = null;
if (typeof window !== 'undefined') {
	postRobotOn = require('@atlassian/bridge-core').postRobotOn;
}

// Internal recursive node shape used by the tree-walk helpers.
// DocNode's nested interfaces lack index signatures, making direct structural
// assignment impossible despite identical runtime shapes, so we bridge via a
// single cast in findConnectBodiedMacrosWithForgeChildren.
type AdfNode = {
	type?: string;
	attrs?: {
		extensionType?: string;
		localId?: string;
		parameters?: {
			macroMetadata?: {
				macroId?: {
					value?: string;
				};
			};
		};
	};
	content?: AdfNode[];
};

const getFausDomain = (): string => {
	// allow all domains in development mode
	return process.env.NODE_ENV === 'development'
		? '*'
		: `${window.location.protocol}//${window.location.hostname}`;
};

const hasForgeDescendant = (nodes: AdfNode[] | undefined): boolean => {
	if (!nodes) {
		return false;
	}
	for (const node of nodes) {
		if (
			(node.type === 'extension' ||
				node.type === 'inlineExtension' ||
				node.type === 'bodiedExtension') &&
			node.attrs?.extensionType === FORGE_EXTENSION_TYPE
		) {
			return true;
		}
		if (hasForgeDescendant(node.content)) {
			return true;
		}
	}
	return false;
};

const getConnectMacroIdentifiers = (node: AdfNode): string[] => {
	const localId = node.attrs?.localId;
	const macroId = node.attrs?.parameters?.macroMetadata?.macroId?.value;

	return [localId, macroId].filter((value): value is string => typeof value === 'string');
};

/**
 * Walk the ADF tree and return identifiers for Connect bodied macros whose
 * body content contains at least one Forge extension.
 *
 * Note: forwarded FAUS messages currently target the Connect macro's
 * `macroMetadata.macroId.value`, not the ADF node `attrs.localId`.
 */
const findConnectBodiedMacrosWithForgeChildren = (adf: DocNode | undefined): Set<string> => {
	const result = new Set<string>();
	if (!adf?.content) {
		return result;
	}

	const walk = (nodes: AdfNode[]) => {
		for (const node of nodes) {
			if (
				node.type === 'bodiedExtension' &&
				node.attrs?.extensionType === CONNECT_MACRO_EXTENSION_TYPE &&
				hasForgeDescendant(node.content)
			) {
				for (const identifier of getConnectMacroIdentifiers(node)) {
					result.add(identifier);
				}
			}
			if (node.content) {
				walk(node.content);
			}
		}
	};

	walk(adf.content as unknown as AdfNode[]);
	return result;
};

/**
 * Walk the ADF tree and return `localId` values for Forge extension nodes
 * (`com.atlassian.ecosystem`) that are nested inside Connect bodied macros.
 *
 * FAUS uses the Forge extension's own `localId` as the channel identifier
 * when forwarding bridge calls via `messageFromEmbeddedFAUS_<localId>`.
 */
const findForgeMacroLocalIds = (adf: DocNode | undefined): Set<string> => {
	const result = new Set<string>();
	if (!adf?.content) {
		return result;
	}

	const walk = (nodes: AdfNode[], insideConnectMacro: boolean) => {
		for (const node of nodes) {
			const isConnectBodiedMacro =
				node.type === 'bodiedExtension' &&
				node.attrs?.extensionType === CONNECT_MACRO_EXTENSION_TYPE;

			if (
				insideConnectMacro &&
				(node.type === 'extension' ||
					node.type === 'inlineExtension' ||
					node.type === 'bodiedExtension') &&
				node.attrs?.extensionType === FORGE_EXTENSION_TYPE &&
				node.attrs?.localId
			) {
				result.add(node.attrs.localId);
			}
			if (node.content) {
				walk(node.content, insideConnectMacro || isConnectBodiedMacro);
			}
		}
	};

	walk(adf.content as unknown as AdfNode[], false);
	return result;
};

type BridgeEventHandler = (payload: Record<string, unknown>) => void;

/**
 * Payload received from an embedded Forge app via the `openModal` postRobot
 * event. Field shapes mirror the Forge bridge `view.modal.open` API.
 *
 * NOTE: This is a structural type used only at the product/bridge boundary.
 * Anything more strongly typed lives in `EmbeddedForgeMacroModal`.
 */
export type EmbeddedForgeOpenModalBridgePayload = {
	extension?: unknown;
	resource?: string;
	size?: string;
	context?: unknown;
	closeOnEscape?: boolean;
	closeOnOverlayClick?: boolean;
	onClose?: (payload?: unknown) => void;
	title?: string;
	icon?: string;
	embeddedMacroLocalId?: string;
	extensionData?: Record<string, unknown>;
	extensionPayload?: Record<string, unknown>;
	isEmbeddedMacroModal?: boolean;
	[key: string]: unknown;
};

export type RegisterEmbeddedMacroListenersOptions = {
	onEmbeddedForgeOpenModal?: (payload: EmbeddedForgeOpenModalBridgePayload) => void;
};

const buildBridgeEventHandlers = (
	flagProvider: ReturnType<typeof getFlagProvider> | null,
	options?: RegisterEmbeddedMacroListenersOptions,
): Record<string, BridgeEventHandler> => {
	const handlers: Record<string, BridgeEventHandler> = {};

	if (flagProvider) {
		handlers.showFlag = (payload) => {
			const actions = payload.actions as Array<{ onClick?: () => void; text: string }> | undefined;
			const flagActions = actions?.map((action) => ({
				...action,
				onClick: action.onClick ? action.onClick : () => {},
			}));
			flagProvider.showFlag({
				...payload,
				actions: flagActions,
			});
		};

		handlers.closeFlag = (payload) => {
			flagProvider.closeFlag(payload);
		};
	}

	if (options?.onEmbeddedForgeOpenModal) {
		const onOpenModal = options.onEmbeddedForgeOpenModal;
		handlers.openModal = (payload) => {
			// `payload.extension` is required to render the modal — if it's
			// missing the bridge call is malformed and we silently drop it
			// rather than rendering an empty modal.
			if (!payload.extension) {
				return;
			}
			onOpenModal(payload as EmbeddedForgeOpenModalBridgePayload);
		};
	}

	return handlers;
};

/**
 * Register postRobot listeners for a single Connect-macro identifier.
 *
 * Sets up a message channel (`messageFromEmbeddedFAUS_<id>`) that dispatches
 * incoming bridge calls to the matching handler, and an analytics channel
 * (`analyticsFromEmbeddedFAUS_<id>`) that acknowledges events to prevent
 * "No handler found" errors from postRobot.
 *
 * @returns An array of cancel functions for every registered listener.
 */
const setupBridgeListeners = (
	on: PostRobotOnFn,
	identifier: string,
	productHostname: string,
	handlers: Record<string, BridgeEventHandler>,
): Array<() => void> => {
	const cancelFns: Array<() => void> = [];
	const originDomain = getFausDomain();

	const messageHandler = on(
		`${EMBEDDED_FAUS_MESSAGE_PREFIX}${identifier}`,
		{
			domain: originDomain,
		},
		(event: PostRobotEvent) => {
			const originUrl = new URL(event.origin);
			if (originUrl.hostname !== productHostname) {
				return Promise.resolve();
			}

			const { eventKey, ...payload } = (event.data ?? {}) as Record<string, unknown>;

			const handler = typeof eventKey === 'string' ? handlers[eventKey] : undefined;
			if (handler) {
				handler(payload);
			}

			return Promise.resolve();
		},
	);

	cancelFns.push(messageHandler.cancel);

	// FAUS also sends a one-shot `analyticsFromEmbeddedFAUS_<id>` message per
	// identifier so embedded Forge apps can forward analytics events (UI, track,
	// operational, screen) to the product. In the normal Forge-in-Forge case this
	// is handled by ForgeUIExtensionPointProvider which has access to the product's
	// AnalyticsWebClient (see ForgeUIExtensionPointProvider.tsx). In the
	// Connect-bodied-macro case there is no parent Forge provider, so we register
	// the listener here to prevent "No handler found" errors from postRobot.
	const analyticsHandler = on(
		`${EMBEDDED_FAUS_ANALYTICS_PREFIX}${identifier}`,
		{
			domain: originDomain,
		},
		(_event: PostRobotEvent) => {
			// TODO: forward events to the Confluence analytics pipeline once an
			// AnalyticsWebClient is plumbed through to this helper.
			// https://product-fabric.atlassian.net/browse/SCALE-8758
			return Promise.resolve();
		},
	);

	cancelFns.push(analyticsHandler.cancel);

	return cancelFns;
};

/**
 * Registers postRobot listeners for every Connect bodied macro on the page
 * that contains an embedded Forge extension.
 *
 * This fills the gap where a Forge app embedded inside a Connect bodied
 * macro forwards bridge calls (showFlag, closeFlag, etc.) but there is no
 * parent Forge bridge to handle them.
 *
 * Optional `onEmbeddedForgeOpenModal` handles `openModal` when feature gates
 * `forge_nbm_connect_to_forge_bridge` is enabled.
 *
 * @returns A cleanup function that removes all listeners, or `undefined` if
 *          no listeners were registered.
 */
export const registerEmbeddedMacroListeners = (
	adf: DocNode | undefined,
	flags: FlagsStateContainer | undefined,
	options?: RegisterEmbeddedMacroListenersOptions,
): (() => void) | undefined => {
	if (!postRobotOn) {
		return;
	}

	const connectMacroIdentifiers = findConnectBodiedMacrosWithForgeChildren(adf);
	const forgeMacroLocalIds = findForgeMacroLocalIds(adf);

	if (connectMacroIdentifiers.size === 0 && forgeMacroLocalIds.size === 0) {
		return;
	}

	const flagProvider = flags ? getFlagProvider(flags) : null;
	const productHostname = window.location.hostname;
	const handlers = buildBridgeEventHandlers(flagProvider, options);

	const cancelFns: Array<() => void> = [];

	for (const identifier of connectMacroIdentifiers) {
		cancelFns.push(...setupBridgeListeners(postRobotOn, identifier, productHostname, handlers));
	}

	for (const forgeMacroLocalId of forgeMacroLocalIds) {
		cancelFns.push(
			...setupBridgeListeners(postRobotOn, forgeMacroLocalId, productHostname, handlers),
		);
	}

	return () => {
		for (const cancel of cancelFns) {
			cancel();
		}
	};
};

export type AppKeyInfoType = {
	connectIdentifier: string;
	connectMacroOrigin: string;
	connectMacroId: string;
};

type AppKeyListenerRegistryEntry = {
	listenerCancel: () => void;
	byWindow: Map<Window, AppKeyInfoType>;
};

const appKeyListenerRegistry = new Map<string, AppKeyListenerRegistryEntry>();

export const resetAppKeyListenerRegistry = (): void => {
	appKeyListenerRegistry.clear();
};

export const registerEmbeddedFausAppKeyListener = (
	bridgeIdentifier: string | undefined,
	macroOrigin: string | undefined,
	macroId: string | undefined,
	targetWindow: Window,
): (() => void) | undefined => {
	if (!postRobotOn || !macroOrigin || !bridgeIdentifier || !macroId) {
		return;
	}

	const requestName = `${EMBEDDED_FAUS_FETCH_APP_KEY_PREFIX}${bridgeIdentifier}`;
	const appKeyInfo: AppKeyInfoType = {
		connectIdentifier: bridgeIdentifier,
		connectMacroOrigin: macroOrigin,
		connectMacroId: macroId,
	};

	const existingEntry = appKeyListenerRegistry.get(requestName);
	if (existingEntry) {
		existingEntry.byWindow.set(targetWindow, appKeyInfo);
		return () => {
			const entry = appKeyListenerRegistry.get(requestName);
			if (!entry) {
				return;
			}
			entry.byWindow.delete(targetWindow);
			if (entry.byWindow.size === 0) {
				entry.listenerCancel();
				appKeyListenerRegistry.delete(requestName);
			}
		};
	}

	const byWindow = new Map<Window, AppKeyInfoType>();
	byWindow.set(targetWindow, appKeyInfo);

	const listener = postRobotOn(
		requestName,
		{
			domain: getFausDomain(),
		},
		(event: PostRobotEvent) => {
			const entry = appKeyListenerRegistry.get(requestName);
			if (!entry || entry.byWindow.size === 0) {
				return Promise.reject(new Error('No listeners registered for request'));
			}

			const responseFromSource = event.source ? entry.byWindow.get(event.source) : undefined;
			const fallbackResponse =
				!responseFromSource && entry.byWindow.size === 1
					? entry.byWindow.values().next().value
					: undefined;
			const response = responseFromSource ?? fallbackResponse;
			if (!response) {
				return Promise.reject(new Error('No listener registered for source window'));
			}
			return Promise.resolve(response);
		},
	);

	appKeyListenerRegistry.set(requestName, {
		listenerCancel: listener.cancel,
		byWindow,
	});

	return () => {
		const entry = appKeyListenerRegistry.get(requestName);
		if (!entry) {
			return;
		}
		entry.byWindow.delete(targetWindow);
		if (entry.byWindow.size === 0) {
			entry.listenerCancel();
			appKeyListenerRegistry.delete(requestName);
		}
	};
};
